import { AtSign, LockKeyhole, Send, Zap } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

import { AlternadorEmPilula, OpcaoDoAlternador } from "@/components/ui/alternador-em-pilula";
import { formErrorClass } from "@/components/ui/atoms";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { mencoesQueFicaram, preencherResposta } from "@/lib/conversas";
import type { RespostaPronta } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Responder ao contato ou Nota interna (só a equipe vê) — só no atendimento. */
export type ModoDoCompositor = "responder" | "nota";

export interface RascunhoDaConversa {
  texto: string;
  modo: ModoDoCompositor;
}

/**
 * TUT-491 — o rascunho é de cada CONVERSA (a regra da TUT-472, agora para as
 * três fontes): mora num `Map` do dono da caixa, fora do estado do React, e o
 * compositor (montado com `key` da conversa) o lê ao nascer e o grava a cada
 * tecla. Trocar de conversa não leva o texto, e voltar o devolve.
 */
export type RascunhosDaCaixa = Map<string, RascunhoDaConversa>;

export interface Mencionavel {
  userId: string;
  nome: string;
}

type Sugestao =
  | { tipo: "resposta"; resposta: RespostaPronta; previa: string }
  | { tipo: "mencao"; pessoa: Mencionavel };

const FALHA_NO_ENVIO = "Não foi possível enviar. A mensagem continua aqui para tentar de novo.";

/**
 * M4 — a recusa do servidor com motivo (400: menção a quem não participa, ou
 * em conversa com alguém de fora) chega à tela com o motivo dele; o resto,
 * com o aviso genérico. O texto continua no campo nos dois casos.
 */
function avisoDaFalha(erro: unknown): string {
  const status = typeof erro === "object" && erro && "status" in erro ? (erro as { status: unknown }).status : null;
  if (status === 400 && erro instanceof Error && erro.message) {
    return `${erro.message} A mensagem continua aqui para tentar de novo.`;
  }
  return FALHA_NO_ENVIO;
}

function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("pt-BR");
}

/** O `/atalho` ou o `@nome` que a pessoa está digitando, até o cursor. */
function gatilhoNoCursor(texto: string, cursor: number): { tipo: "/" | "@"; inicio: number; consulta: string } | null {
  const antes = texto.slice(0, cursor);
  const barra = /(^|\s)\/([\w-]*)$/u.exec(antes);
  if (barra) return { tipo: "/", inicio: antes.length - barra[2]!.length - 1, consulta: barra[2]! };
  const arroba = /(^|\s)@([^\n@]{0,40})$/u.exec(antes);
  if (arroba) return { tipo: "@", inicio: antes.length - arroba[2]!.length - 1, consulta: arroba[2]! };
  return null;
}

export function CompositorDaCaixa({
  conversaId,
  rascunhos,
  idDoCampo,
  rotulo,
  placeholder,
  textoDoBotao,
  limite,
  enviando,
  aviso,
  comModos = false,
  respostas,
  contexto,
  mencionaveis,
  compacto = false,
  onEnviar,
}: {
  conversaId: string;
  rascunhos: RascunhosDaCaixa;
  idDoCampo: string;
  rotulo: (modo: ModoDoCompositor) => string;
  placeholder: (modo: ModoDoCompositor) => string;
  textoDoBotao: (modo: ModoDoCompositor) => string;
  limite: number;
  enviando: boolean;
  aviso?: (modo: ModoDoCompositor) => ReactNode;
  /** Mostra "Responder contato" × "Nota interna" (o atendimento). */
  comModos?: boolean;
  /** Liga o `/` das respostas prontas (no responder do atendimento e no portal). */
  respostas?: readonly RespostaPronta[] | null;
  contexto?: { nome: string | null; processo: string | null };
  /** Liga o `@` (na conversa da equipe): só quem participa. */
  mencionaveis?: readonly Mencionavel[] | null;
  /** TUT-590 — a janela do chat rápido: campo de 2 linhas, sem a dica do Enter. */
  compacto?: boolean;
  onEnviar: (texto: string, modo: ModoDoCompositor, mencoes: string[]) => Promise<unknown>;
}) {
  const inicial = rascunhos.get(conversaId);
  const [texto, setTextoLocal] = useState(inicial?.texto ?? "");
  const [modo, setModoLocal] = useState<ModoDoCompositor>(inicial?.modo ?? "responder");
  const [erro, setErro] = useState("");
  const [faltou, setFaltou] = useState<string[]>([]);
  const [gatilho, setGatilho] = useState<ReturnType<typeof gatilhoNoCursor>>(null);
  const [indice, setIndice] = useState(0);
  const campo = useRef<HTMLTextAreaElement | null>(null);
  const escolhidas = useRef<Mencionavel[]>([]);
  const vivo = useRef(true);
  const idDaLista = useId();

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  function gravar(proximo: Partial<RascunhoDaConversa>) {
    const atual = rascunhos.get(conversaId) ?? { texto: "", modo: "responder" };
    rascunhos.set(conversaId, { ...atual, ...proximo });
  }
  function setTexto(valor: string) {
    setTextoLocal(valor);
    gravar({ texto: valor });
  }
  function setModo(valor: ModoDoCompositor) {
    setModoLocal(valor);
    gravar({ modo: valor });
    setErro("");
  }

  const prontasLigadas = Boolean(respostas) && (!comModos || modo === "responder");
  const mencoesLigadas = Boolean(mencionaveis?.length);

  const sugestoes: Sugestao[] = (() => {
    if (!gatilho) return [];
    const consulta = normalizar(gatilho.consulta);
    if (gatilho.tipo === "/" && prontasLigadas) {
      return (respostas ?? [])
        .filter((r) => r.atalho.includes(consulta) || normalizar(r.titulo).includes(consulta))
        .map((resposta) => ({
          tipo: "resposta" as const,
          resposta,
          previa: preencherResposta(resposta.texto, contexto ?? { nome: null, processo: null }).texto,
        }));
    }
    if (gatilho.tipo === "@" && mencoesLigadas) {
      return (mencionaveis ?? [])
        .filter((p) => normalizar(p.nome).includes(consulta))
        .map((pessoa) => ({ tipo: "mencao" as const, pessoa }));
    }
    return [];
  })();
  const listaAberta = sugestoes.length > 0;

  function reavaliar(valor: string, cursor: number) {
    setGatilho(gatilhoNoCursor(valor, cursor));
    setIndice(0);
  }

  function escolher(sugestao: Sugestao) {
    if (!gatilho) return;
    const cursor = campo.current?.selectionStart ?? texto.length;
    const antes = texto.slice(0, gatilho.inicio);
    const depois = texto.slice(cursor);
    let inserido: string;
    if (sugestao.tipo === "resposta") {
      const preenchida = preencherResposta(sugestao.resposta.texto, contexto ?? { nome: null, processo: null });
      inserido = preenchida.texto;
      setFaltou(preenchida.faltou);
    } else {
      inserido = `@${sugestao.pessoa.nome} `;
      escolhidas.current = [...escolhidas.current, sugestao.pessoa];
    }
    const novo = `${antes}${inserido}${depois}`;
    setTexto(novo);
    setGatilho(null);
    const posicao = antes.length + inserido.length;
    requestAnimationFrame(() => {
      campo.current?.focus();
      campo.current?.setSelectionRange(posicao, posicao);
    });
  }

  function inserirGatilho(caractere: "/" | "@") {
    const alvo = campo.current;
    const cursor = alvo?.selectionStart ?? texto.length;
    const separador = cursor > 0 && !/\s/u.test(texto[cursor - 1] ?? "") ? " " : "";
    const novo = `${texto.slice(0, cursor)}${separador}${caractere}${texto.slice(cursor)}`;
    setTexto(novo);
    const posicao = cursor + separador.length + 1;
    reavaliar(novo, posicao);
    requestAnimationFrame(() => {
      alvo?.focus();
      alvo?.setSelectionRange(posicao, posicao);
    });
  }

  function enviar() {
    const corpo = texto.trim();
    if (!corpo || enviando) return;
    setErro("");
    const mencoes = mencoesLigadas ? mencoesQueFicaram(corpo, escolhidas.current) : [];
    onEnviar(corpo, modo, mencoes).then(
      () => {
        // Sai só o que foi enviado: o que a pessoa digitou enquanto esperava
        // fica (TUT-395); e se ela já trocou de conversa, o rascunho DESTA
        // conversa é que perde o texto, não o da que está aberta (TUT-472).
        if (vivo.current) {
          setTextoLocal((atual) => (atual.trim() === corpo ? "" : atual));
          setFaltou([]);
          escolhidas.current = [];
        }
        const guardado = rascunhos.get(conversaId);
        if (guardado && guardado.texto.trim() === corpo) rascunhos.set(conversaId, { ...guardado, texto: "" });
      },
      (falha: unknown) => {
        if (vivo.current) setErro(avisoDaFalha(falha));
      },
    );
  }

  function aoTeclar(evento: KeyboardEvent<HTMLTextAreaElement>) {
    if (listaAberta) {
      if (evento.key === "ArrowDown" || evento.key === "ArrowUp") {
        evento.preventDefault();
        const passo = evento.key === "ArrowDown" ? 1 : -1;
        setIndice((atual) => (atual + passo + sugestoes.length) % sugestoes.length);
        return;
      }
      if (evento.key === "Enter" || evento.key === "Tab") {
        evento.preventDefault();
        const escolhida = sugestoes[indice];
        if (escolhida) escolher(escolhida);
        return;
      }
      if (evento.key === "Escape") {
        evento.preventDefault();
        setGatilho(null);
        return;
      }
    }
    if (evento.key === "Enter" && !evento.shiftKey) {
      evento.preventDefault();
      enviar();
    }
  }

  const nota = comModos && modo === "nota";
  const idDaOpcao = (i: number) => `${idDaLista}-${i}`;

  return (
    <form
      className={cn("border-t border-border bg-surface", compacto ? "p-3" : "p-4")}
      data-compositor-da-caixa
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      {comModos ? (
        // TUT-511 — o tipo de envio é um alternador em pílula (Lumen, P34).
        <AlternadorEmPilula rotulo="Tipo de envio" className="mb-3">
          <OpcaoDoAlternador escolhida={modo === "responder"} onClick={() => setModo("responder")}>
            <Send aria-hidden="true" />
            Responder contato
          </OpcaoDoAlternador>
          <OpcaoDoAlternador escolhida={modo === "nota"} onClick={() => setModo("nota")}>
            <LockKeyhole aria-hidden="true" />
            Nota interna
          </OpcaoDoAlternador>
        </AlternadorEmPilula>
      ) : null}
      <label htmlFor={idDoCampo} className="mb-1 block text-legenda font-semibold text-[var(--muted)]">
        {rotulo(modo)}
      </label>
      <div className="relative">
        <Textarea
          id={idDoCampo}
          ref={campo}
          rows={compacto ? 2 : 3}
          maxLength={limite}
          value={texto}
          placeholder={placeholder(modo)}
          aria-controls={listaAberta ? idDaLista : undefined}
          className={cn(nota && "border-dashed bg-[var(--warning-highlight)]")}
          onChange={(evento) => {
            setTexto(evento.target.value);
            reavaliar(evento.target.value, evento.target.selectionStart ?? evento.target.value.length);
          }}
          onKeyDown={aoTeclar}
          onBlur={() => setGatilho(null)}
        />
        {listaAberta ? (
          // As sugestões são botões numa lista: o teclado fica no campo (setas,
          // Enter, Tab, Esc) e o mouse clica. Sem `listbox` à mão (o lint da
          // casa pede o elemento nativo, que não serve a um campo de texto livre).
          <ul
            id={idDaLista}
            aria-label={gatilho?.tipo === "/" ? "Respostas prontas" : "Mencionar alguém da conversa"}
            className="absolute inset-x-0 bottom-full z-20 mb-1 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface p-1 shadow-lg"
          >
            {sugestoes.map((sugestao, i) => (
              <li key={sugestao.tipo === "resposta" ? sugestao.resposta.id : sugestao.pessoa.userId}>
                <button
                  type="button"
                  id={idDaOpcao(i)}
                  tabIndex={-1}
                  aria-current={i === indice ? "true" : undefined}
                  className={cn(
                    "block w-full cursor-pointer rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-corpo",
                    i === indice && "bg-[var(--primary-highlight)]",
                  )}
                  onMouseDown={(evento) => {
                    evento.preventDefault();
                    escolher(sugestao);
                  }}
                >
                {sugestao.tipo === "resposta" ? (
                  <>
                    <span className="font-semibold">/{sugestao.resposta.atalho}</span> · {sugestao.resposta.titulo}
                    <span className="block truncate text-legenda text-[var(--muted)]">{sugestao.previa}</span>
                  </>
                ) : (
                  <span className="font-semibold">{sugestao.pessoa.nome}</span>
                )}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {faltou.length ? (
        <p aria-live="polite" className="m-0 mt-1 text-legenda text-[var(--warning)]">
          Falta preencher {faltou.map((chave) => `{${chave}}`).join(", ")}: esta conversa não tem esse dado.
        </p>
      ) : null}
      {erro ? (
        <p role="alert" className={`mt-2 ${formErrorClass}`}>
          {erro}
        </p>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {prontasLigadas ? (
          <Button type="button" variant="discreto" size="sm" onClick={() => inserirGatilho("/")}>
            <Zap aria-hidden="true" />
            Respostas prontas
          </Button>
        ) : null}
        {mencoesLigadas ? (
          <Button type="button" variant="discreto" size="sm" onClick={() => inserirGatilho("@")}>
            <AtSign aria-hidden="true" />
            Mencionar
          </Button>
        ) : null}
        {compacto ? null : <span className="text-legenda text-[var(--muted)]">Enter envia · Shift+Enter quebra a linha</span>}
        <Button type="submit" className="ml-auto" disabled={enviando || texto.trim().length === 0}>
          {nota ? <LockKeyhole aria-hidden="true" /> : <Send aria-hidden="true" />}
          {enviando ? "Enviando..." : textoDoBotao(modo)}
        </Button>
      </div>
      {aviso ? <p className="m-0 mt-2 text-legenda text-[var(--muted)]">{aviso(modo)}</p> : null}
    </form>
  );
}
